import React, { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const SelectedResponse = createContext(null);
// The endpoint workspace's response card, a node beside the operation card. Without one, responses render in place.
export const ResponseSlot = createContext(null);
// Status code buttons: success codes green, client and server errors red.
const codeTone = code => /^2/.test(code) ? 'is-success' : /^[45]/.test(code) ? 'is-error' : undefined;
// Keep the full response map intact: Swagger uses it to choose the Accept header.
// Only the rendered rows change when browsing documented status codes.
export const responseWrappers = {
  responses: Original => function ResponseExplorer(props) {
    const slot = useContext(ResponseSlot);
    const codes = props.responses.keySeq().toArray().filter(code => !code.startsWith('x-'));
    const [selection, setSelection] = useState(null);
    const [showLive, setShowLive] = useState(Boolean(props.tryItOutResponse));
    useEffect(() => { setShowLive(Boolean(props.tryItOutResponse)); }, [props.tryItOutResponse]);
    const active = codes.includes(selection) ? selection : codes[0];
    // Examples take the tone of the selected code; a live result is toned by its own status (see liveResponse).
    const tone = showLive ? '' : codeTone(active) || '';
    const section = <section className={`response-explorer ${showLive ? 'live-result-mode' : ''} ${tone}`} aria-label="Response documentation">
      {/* The title with the Live response / Examples toggle on its right; the status codes get a row of their own. */}
      <div className="response-picker">
        <div className="response-picker-head"><strong>{showLive ? 'Request result' : 'Response examples'}</strong>
          {props.tryItOutResponse && <div className="response-options" role="group" aria-label="Response view">
            <button aria-pressed={showLive} onClick={() => setShowLive(true)}>Live response</button>
            <button aria-pressed={!showLive} onClick={() => setShowLive(false)}>Examples</button>
          </div>}
        </div>
        {!showLive && <div className="response-options response-codes" role="group" aria-label="Example status code">{codes.map(code =>
          <button key={code} type="button" className={codeTone(code)} aria-pressed={active === code} onClick={() => setSelection(code)}>{code}</button>)}</div>}
      </div>
      <SelectedResponse.Provider value={active}><Original {...props} tryItOutResponse={showLive ? props.tryItOutResponse : null} /></SelectedResponse.Provider>
    </section>;
    return slot ? createPortal(section, slot) : section;
  },
  response: Original => function ResponseRow(props) {
    const selected = useContext(SelectedResponse);
    return selected && props.code !== selected ? null : <Original {...props} />;
  },
  // Green for a 2xx result, red for anything else, including a request that got no response at all.
  liveResponse: Original => function LiveResponse(props) {
    const tone = codeTone(String(props.response?.get?.('status') ?? '')) === 'is-success' ? 'is-success' : 'is-error';
    return <section className={`live-response ${tone}`} aria-label="Live response"><h3>Live response</h3><p>Result from the executed request.</p><Original {...props} /></section>;
  },
};
